import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeftRight,
  Search,
  Truck,
  Phone,
  MapPin,
  Calendar,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  ScanBarcode,
  Eye,
  X,
  Package,
  ShoppingBag,
  AlertTriangle,
  CheckCircle2,
  Image as ImageIcon,
} from 'lucide-react';
import {
  fetchExchanges,
  fetchShippingCarriers,
  fetchExchangeDetail,
  type ExchangeDetailResponse,
} from '../api.js';
import { PageHeader } from '../ui/PageHeader.js';
import { Button } from '../ui/Button.js';
import { Stat } from '../ui/data.js';
import { Panel } from '../ui/panels.js';
import { TableShell, Table, THead, TBody, Tr, Th, Td } from '../ui/table.js';
import { Loading } from '../ui/feedback.js';
import { formatTnd, playBeep } from '../lib/format.js';



function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatPhone(phone: string | null | undefined): string {
  if (!phone) return '—';
  const clean = phone.trim();
  const digits = clean.replace(/\D/g, '');
  let d8 = digits;
  if (digits.startsWith('216') && digits.length === 11) {
    d8 = digits.slice(3);
  } else if (digits.length === 8) {
    d8 = digits;
  } else if (digits.length > 8) {
    d8 = digits.slice(-8);
  }
  if (d8.length === 8) {
    return `+216 ${d8.slice(0, 2)} ${d8.slice(2, 5)} ${d8.slice(5, 8)}`;
  }
  return clean;
}

export function ExchangesPage(): React.ReactElement {
  const [carrier, setCarrier] = useState<string>('all');
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const limit = 25;

  // Active Scanner & Inspection Modal State
  const [scannerCode, setScannerCode] = useState<string>('');
  const [selectedBarcode, setSelectedBarcode] = useState<string | null>(null);
  const [modalSearchInput, setModalSearchInput] = useState<string>('');
  const scanInputRef = useRef<HTMLInputElement>(null);

  const carriersQuery = useQuery({
    queryKey: ['shipping', 'carriers'],
    queryFn: fetchShippingCarriers,
  });

  const exchangesQuery = useQuery({
    queryKey: ['shipping', 'echanges', carrier, search, page],
    queryFn: () =>
      fetchExchanges({
        ...(carrier !== 'all' ? { carrier } : {}),
        ...(search.trim() ? { search: search.trim() } : {}),
        page,
        limit,
      }),
  });

  // Query for exchange detail modal
  const exchangeDetailQuery = useQuery<ExchangeDetailResponse>({
    queryKey: ['shipping', 'exchange-detail', selectedBarcode],
    queryFn: () => fetchExchangeDetail(selectedBarcode!),
    enabled: Boolean(selectedBarcode),
  });

  const kpis = exchangesQuery.data?.kpis;
  const parcels = exchangesQuery.data?.parcels ?? [];
  const totalCount = exchangesQuery.data?.totalCount ?? 0;
  const totalPages = exchangesQuery.data?.totalPages ?? 1;

  // Handle direct barcode scan
  const handleScannerSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const clean = scannerCode.trim();
    if (!clean) return;
    setScannerCode('');
    setSelectedBarcode(clean);
  };

  return (
    <div className="space-y-6 animate-fade">
      <PageHeader
        index="S38"
        eyebrow="Gestion Logistique · Échanges Client"
        title="Workspace Échanges Client (État 3)"
        purpose="Espace dédié au contrôle des colis d'échange : comparaison entre ce qui a été envoyé et ce que le transporteur doit rapporter du client avec photo du produit."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (parcels.length === 0) return;
                const headers = ['Code-barre', 'Client', 'Telephone', 'Ville', 'Montant', 'Presta', 'Date'];
                const rows = parcels.map((p) => [
                  p.codeBarre,
                  `"${p.clientName ?? (p as unknown as Record<string, unknown>).nom ?? ''}"`,
                  `"${p.clientPhone ?? (p as unknown as Record<string, unknown>).tel ?? ''}"`,
                  `"${p.location ?? (p as unknown as Record<string, unknown>).ville ?? ''}"`,
                  p.shiparonPrice ?? (p as unknown as Record<string, unknown>).prix ?? '',
                  p.carrier ?? (p as unknown as Record<string, unknown>).presta ?? '',
                  p.date ?? '',
                ]);
                const csvContent = [headers.join(';'), ...rows.map((r) => r.join(';'))].join('\n');
                const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `echanges_clients_${new Date().toISOString().slice(0, 10)}.csv`;
                a.click();
              }}
              className="text-ink-soft hover:text-ink"
            >
              <FileSpreadsheet className="h-4 w-4 mr-1.5" />
              Exporter CSV
            </Button>
          </div>
        }
      />
      
      {/* Active Barcode Scanner Banner for Exchange Inspection */}
      <Panel className="p-4 border-indigo-500/30 bg-gradient-to-r from-indigo-500/5 via-card to-card shadow-xs">
        <form onSubmit={handleScannerSubmit} className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 w-full sm:w-auto">
            <div className="p-2 rounded-xl bg-indigo-600 text-white shadow-xs shrink-0">
              <ScanBarcode className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-ink">Inspecteur d'Échange (Flasheur)</h3>
              <p className="text-xs text-ink-soft">
                Flashez le code-barre d'un colis d'échange pour afficher la photo et la référence exacte à récupérer.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <input
              ref={scanInputRef}
              type="text"
              value={scannerCode}
              onChange={(e) => setScannerCode(e.target.value)}
              placeholder="Flasher code-barre (ex: JP123456)..."
              className="text-xs font-mono px-3 py-2 rounded-lg border border-border bg-card text-ink focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 w-full sm:w-64"
            />
            <Button
              type="submit"
              variant="primary"
              size="sm"
              disabled={!scannerCode.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0 font-semibold"
            >
              Inspecter [Enter]
            </Button>
          </div>
        </form>
      </Panel>

      {/* KPI Cards (Clickable to filter) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat
          label="Total Colis Échange"
          value={(kpis?.total ?? 0).toLocaleString('fr-FR')}
          hint="Tous les colis État 3 (cliquer pour réinitialiser)"
          onClick={() => {
            setCarrier('all');
            setSearch('');
            setPage(1);
          }}
        />
        <Stat
          label="Valeur Marchandise Échange"
          value={formatTnd(kpis?.totalAmount ?? 0)}
          hint="Montant cumulé COD"
        />
        <Stat
          label="Échanges Livrés"
          value={(kpis?.delivered ?? 0).toLocaleString('fr-FR')}
          hint="Livrées et reprises effectuées"
          tone="good"
          onClick={() => {
            setSearch('livré');
            setPage(1);
          }}
        />
        <Stat
          label="En Cours / En Transit"
          value={(kpis?.inTransit ?? 0).toLocaleString('fr-FR')}
          hint="En cours de livraison / reprise"
          tone="signal"
          onClick={() => {
            setSearch('en cours');
            setPage(1);
          }}
        />
      </div>

      {/* Filters Toolbar */}
      <Panel>
        <div className="p-4 flex flex-wrap items-center justify-between gap-3 border-b border-border">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative min-w-[240px]">
              <Search className="h-4 w-4 text-ink-ghost absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Rechercher code-barres, client, tel…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full text-xs pl-9 pr-3 py-1.5 rounded-lg border border-border bg-card text-ink focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none"
              />
            </div>

            {/* Carrier select */}
            <select
              value={carrier}
              onChange={(e) => {
                setCarrier(e.target.value);
                setPage(1);
              }}
              className="text-xs rounded-lg px-2.5 py-1.5 border border-border bg-card text-ink font-medium cursor-pointer"
            >
              <option value="all">Tous transporteurs</option>
              {carriersQuery.data?.carriers.map((c) => (
                <option key={c.carrier} value={c.carrier}>
                  {c.carrier} ({c.count})
                </option>
              ))}
            </select>
          </div>

          <div className="text-xs text-ink-soft">
            <strong>{totalCount.toLocaleString('fr-FR')}</strong> échange(s) trouvé(s)
          </div>
        </div>

        {/* Table of exchange parcels */}
        <TableShell>
          <Table>
            <THead>
              <Tr>
                <Th>Code-Barres & Facture</Th>
                <Th>Client & Téléphone</Th>
                <Th>Destination</Th>
                <Th>Montant COD</Th>
                <Th>Transporteur</Th>
                <Th>Date Création</Th>
                <Th className="text-right">Inspection Produits</Th>
              </Tr>
            </THead>
            <TBody>
              {exchangesQuery.isLoading ? (
                <Tr>
                  <Td colSpan={7} className="text-center py-8">
                    <Loading label="Chargement des échanges…" />
                  </Td>
                </Tr>
              ) : parcels.length === 0 ? (
                <Tr>
                  <Td colSpan={7} className="text-center py-8 text-ink-soft text-xs">
                    Aucun colis d'échange trouvé avec ces critères.
                  </Td>
                </Tr>
              ) : (
                parcels.map((parcel) => {
                  const clientName = parcel.clientName || (parcel as unknown as Record<string, unknown>).nom as string || 'Client';
                  const clientPhone = parcel.clientPhone || (parcel as unknown as Record<string, unknown>).tel as string || null;
                  const location = parcel.location || (parcel as unknown as Record<string, unknown>).ville as string || '—';
                  const price = parcel.shiparonPrice ?? (parcel as unknown as Record<string, unknown>).prix as number ?? null;
                  const presta = parcel.carrier || (parcel as unknown as Record<string, unknown>).presta as string || 'Non assigné';

                  return (
                    <Tr key={parcel.codeBarre} className="hover:bg-cream/40 transition-colors">
                      <Td className="font-mono font-bold text-xs text-ink">
                        <div className="flex items-center gap-1.5">
                          <ArrowLeftRight className="h-3.5 w-3.5 text-indigo-700 shrink-0" />
                          <span>{parcel.codeBarre}</span>
                        </div>
                        {parcel.facture && (
                          <span className="text-[11px] text-ink-soft font-normal block font-sans">
                            Facture: {parcel.facture}
                          </span>
                        )}
                      </Td>
                      <Td className="text-xs">
                        <p className="font-semibold text-ink">{clientName}</p>
                        {clientPhone && (
                          <p className="text-[11px] text-ink-soft flex items-center gap-1 mt-0.5 font-mono">
                            <Phone className="h-3 w-3 text-ink-ghost" />
                            {formatPhone(clientPhone)}
                          </p>
                        )}
                      </Td>
                      <Td className="text-xs text-ink-soft">
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3 text-ink-ghost shrink-0" />
                          {location}
                        </span>
                      </Td>
                      <Td className="text-xs font-bold text-ink whitespace-nowrap">
                        {formatTnd(price)}
                      </Td>
                      <Td className="text-xs font-medium text-ink">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-cream text-ink border border-border/80">
                          <Truck className="h-3 w-3 text-ink-soft" />
                          {presta}
                        </span>
                      </Td>
                      <Td className="text-xs text-ink-soft whitespace-nowrap">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-ink-ghost" />
                          {formatDate(parcel.date)}
                        </span>
                      </Td>
                      <Td className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            playBeep('success');
                            setSelectedBarcode(parcel.codeBarre);
                          }}
                          className="text-xs font-semibold text-indigo-700 border-indigo-300 hover:bg-indigo-50 shadow-2xs"
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          Inspecter Photo
                        </Button>
                      </Td>
                    </Tr>
                  );
                })
              )}
            </TBody>
          </Table>
        </TableShell>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-border flex items-center justify-between text-xs">
            <span className="text-ink-soft">
              Page {page} sur {totalPages}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Précédent
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Suivant <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Panel>

      {/* Centered Exchange Inspection Modal (Portal to document.body) */}
      {selectedBarcode &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 overflow-y-auto animate-fade"
            onClick={() => setSelectedBarcode(null)}
          >
            <div
              role="dialog"
              aria-modal="true"
              className="relative bg-card border border-border rounded-2xl p-6 max-w-3xl w-full shadow-2xl space-y-6 max-h-[90vh] overflow-y-auto my-auto animate-in zoom-in-95 duration-150"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between pb-3 border-b border-border">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-indigo-600 text-white shadow-xs">
                    <ArrowLeftRight className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-ink">
                      Contrôle Échange Colis : <span className="font-mono text-indigo-700">{selectedBarcode}</span>
                    </h3>
                    <p className="text-xs text-ink-soft">
                      Inspection comparative entre l'envoi client et l'article à récupérer physiquement.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedBarcode(null)}
                  className="text-ink-ghost hover:text-ink p-1 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {exchangeDetailQuery.isLoading ? (
                <div className="py-12 text-center">
                  <Loading label="Recherche des données de commande et du catalogue produit…" />
                </div>
              ) : exchangeDetailQuery.isError ? (
                <div className="p-6 rounded-2xl bg-rust-50 border border-rust-300 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-xl bg-rust-100 text-rust-700 shrink-0">
                      <AlertTriangle className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-rust-950">Colis d'échange introuvable</h4>
                      <p className="text-xs text-rust-900 mt-0.5 leading-relaxed">
                        Le code <span className="font-mono font-bold text-rust-950">« {selectedBarcode} »</span> n'a pas été trouvé dans la base des colis d'échange ou factures associées.
                      </p>
                    </div>
                  </div>

                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const clean = modalSearchInput.trim();
                      if (clean) {
                        setSelectedBarcode(clean);
                        setModalSearchInput('');
                      }
                    }}
                    className="flex flex-col sm:flex-row items-center gap-2 pt-2 border-t border-rust-200"
                  >
                    <input
                      type="text"
                      value={modalSearchInput}
                      onChange={(e) => setModalSearchInput(e.target.value)}
                      placeholder="Réessayer avec un code-barres (ex: JP123456) ou facture…"
                      className="text-xs font-mono px-3 py-2 rounded-lg border border-rust-300 bg-white text-ink focus:outline-none focus:ring-2 focus:ring-rust-500/20 focus:border-rust-600 w-full sm:flex-1"
                    />
                    <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                      <Button
                        type="submit"
                        variant="primary"
                        size="sm"
                        disabled={!modalSearchInput.trim()}
                        className="bg-rust-700 hover:bg-rust-800 text-white font-semibold"
                      >
                        Rechercher
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedBarcode(null)}
                      >
                        Fermer
                      </Button>
                    </div>
                  </form>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Two Main Columns: Ce qui a été envoyé vs Ce que le livreur doit rapporter */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* SECTION 1: Ce qui a été envoyé au client */}
                    <div className="p-4 rounded-xl border border-border bg-cream/40 space-y-3">
                      <div className="flex items-center justify-between border-b border-border/80 pb-2">
                        <div className="flex items-center gap-1.5 text-ink font-bold text-xs uppercase tracking-wider">
                          <ShoppingBag className="h-4 w-4 text-emerald-700" />
                          <span>1. Ce qui a été envoyé</span>
                        </div>
                        <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-card border border-border font-bold text-ink">
                          {exchangeDetailQuery.data?.wooOrder ? `Commande #${exchangeDetailQuery.data.wooOrder.number}` : 'Commande liée'}
                        </span>
                      </div>

                      <div className="text-xs space-y-1.5">
                        <div className="flex justify-between">
                          <span className="text-ink-soft">Destinataire :</span>
                          <span className="font-semibold text-ink">
                            {exchangeDetailQuery.data?.wooOrder?.customerName ||
                              exchangeDetailQuery.data?.parcel.clientName ||
                              'Client'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-ink-soft">Téléphone :</span>
                          <span className="font-mono text-ink">
                            {formatPhone(
                              exchangeDetailQuery.data?.wooOrder?.billingPhone ||
                                exchangeDetailQuery.data?.parcel.clientPhone,
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-ink-soft">Montant COD colis :</span>
                          <span className="font-bold text-emerald-800">
                            {formatTnd(exchangeDetailQuery.data?.parcel.shiparonPrice)}
                          </span>
                        </div>
                      </div>

                      {/* Items Sent */}
                      <div className="pt-2 border-t border-border/60 space-y-1.5">
                        <p className="text-[11px] font-bold text-ink-soft uppercase tracking-wider">Articles Expédiés :</p>
                        {Array.isArray(exchangeDetailQuery.data?.wooOrder?.lineItems) &&
                        (exchangeDetailQuery.data.wooOrder.lineItems as Array<{ name: string; quantity: number; total: string }>).length > 0 ? (
                          <div className="space-y-1 max-h-40 overflow-y-auto">
                            {(exchangeDetailQuery.data.wooOrder.lineItems as Array<{ name: string; quantity: number; total: string }>).map((item, idx) => (
                              <div key={idx} className="p-2 rounded-lg bg-card border border-border/60 text-xs flex justify-between items-center">
                                <span className="font-medium text-ink truncate mr-2">{item.name}</span>
                                <span className="font-mono text-ink-soft shrink-0">x{item.quantity}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-ink-soft italic bg-card p-2 rounded-lg border border-border/60">
                            Articles expédiés : {exchangeDetailQuery.data?.parcel.msg || 'Non détaillé dans la base'}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* SECTION 2: Ce que le livreur doit récupérer / rapporter */}
                    <div className="p-4 rounded-xl border-2 border-indigo-400 bg-indigo-50/30 space-y-3">
                      <div className="flex items-center justify-between border-b border-indigo-200 pb-2">
                        <div className="flex items-center gap-1.5 text-indigo-950 font-bold text-xs uppercase tracking-wider">
                          <Package className="h-4 w-4 text-indigo-700" />
                          <span>2. Ce que le livreur doit rapporter</span>
                        </div>
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-indigo-200 text-indigo-900">
                          À récupérer
                        </span>
                      </div>

                      {/* Matched Product Photo & Catalog Reference */}
                      {exchangeDetailQuery.data?.exchangeReturn.product ? (
                        <div className="space-y-3">
                          <div className="flex gap-3 items-start bg-card p-3 rounded-xl border border-indigo-200 shadow-2xs">
                            {exchangeDetailQuery.data.exchangeReturn.product.imageUrl ? (
                              <img
                                src={exchangeDetailQuery.data.exchangeReturn.product.imageUrl}
                                alt={exchangeDetailQuery.data.exchangeReturn.product.name}
                                className="w-20 h-20 object-contain rounded-lg border border-border bg-white shrink-0"
                              />
                            ) : (
                              <div className="w-20 h-20 rounded-lg bg-cream flex flex-col items-center justify-center text-ink-ghost shrink-0 border border-border">
                                <ImageIcon className="h-6 w-6" />
                                <span className="text-[10px] mt-1">Sans photo</span>
                              </div>
                            )}
                            <div className="space-y-1 min-w-0">
                              <p className="font-bold text-xs text-ink leading-tight">
                                {exchangeDetailQuery.data.exchangeReturn.product.name}
                              </p>
                              {exchangeDetailQuery.data.exchangeReturn.product.brand && (
                                <p className="text-[11px] font-semibold text-indigo-800">
                                  Marque : {exchangeDetailQuery.data.exchangeReturn.product.brand}
                                </p>
                              )}
                              {exchangeDetailQuery.data.exchangeReturn.product.sku && (
                                <p className="text-[11px] font-mono text-ink-soft">
                                  SKU / Réf : {exchangeDetailQuery.data.exchangeReturn.product.sku}
                                </p>
                              )}
                              {exchangeDetailQuery.data.exchangeReturn.product.price && (
                                <p className="text-[11px] font-bold text-emerald-800">
                                  Prix Catalogue : {formatTnd(exchangeDetailQuery.data.exchangeReturn.product.price)}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Raw Note / Instructions */}
                          <div className="text-xs bg-card p-2.5 rounded-lg border border-indigo-200/80">
                            <span className="font-bold text-ink-soft block text-[10px] uppercase">Consigne / Motif Échange :</span>
                            <span className="text-ink font-medium">
                              {exchangeDetailQuery.data.exchangeReturn.rawNote}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="bg-card p-4 rounded-xl border border-indigo-200 text-center space-y-2">
                            <Package className="h-8 w-8 text-indigo-400 mx-auto" />
                            <p className="text-xs font-semibold text-ink">
                              Article spécifié dans la fiche transporteur :
                            </p>
                            <p className="text-sm font-bold text-indigo-900 bg-indigo-50 p-2 rounded-lg border border-indigo-200">
                              « {exchangeDetailQuery.data?.exchangeReturn.rawNote || 'Article à récupérer'} »
                            </p>
                            <p className="text-[11px] text-ink-soft">
                              Aucune fiche catalogue automatique associée à ce libellé.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quality Checklist Alert for Operator */}
                  <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-300 text-amber-950 flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                    <div className="text-xs space-y-1">
                      <p className="font-bold text-amber-900">
                        Consignes Obligatoires pour l'Opérateur Réception MaPara :
                      </p>
                      <ul className="list-disc pl-4 space-y-0.5 text-amber-900/90 leading-relaxed">
                        <li>Vérifier que l'article rapporté par le livreur est intact, propre et non descellé.</li>
                        <li>Contrôler la conformité du produit avec la référence et la photo ci-dessus.</li>
                        <li>Ne pas valider l'échange si le produit rapporté est incomplet ou endommagé.</li>
                      </ul>
                    </div>
                  </div>

                  {/* Modal Footer Actions */}
                  <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedBarcode(null)}
                    >
                      Fermer
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold"
                      onClick={() => {
                        setSelectedBarcode(null);
                      }}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1.5" />
                      Contrôle Échange Validé
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
